import { Outlet, useNavigate, useOutletContext } from 'react-router-dom'

import { useApolloClient } from '@apollo/client'
import { useUnsafeCallback } from '@factorialco/deprecated-design-system'
import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import {
  AvatarNameSelectorDocument,
  CostCentersEmployeesStructuredAssignmentsDocument,
  CostCentersEmployeesStructuredAssignmentsQuery,
  CostCentersListDocument,
  EmployeesTeamsDocument,
} from 'generated/resources/graphql'
import moment from 'moment'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { buildUrl as buildAvatarUrl } from 'modules/core/lib/buildUrl'
import i18n from 'modules/core/lib/i18n'

import { CostCenterEmployeeWithStructuredAssignment } from 'modules/cost_centers/components/Resources/Employees/types'
import { CostCenterDetails } from 'modules/cost_centers/lib/types'

import {
  TextValue,
  familyLabelForChild,
  familyLabelForParent,
  functionLabelForChild,
  functionLabelForParent,
  roleLabelForChild,
  roleLabelForParent,
} from './labels'

const ACTIVE = 'active'
const TERMINATED = 'terminated'

interface Filters {
  withCostCenterAssignments?: boolean[]
  legalEntityIds?: number[]
  teamIds?: number[]
  costCenterIds?: number[]
  employeeIds?: number[]
  peopleStatus?: string[]
}

type FormattedCostCenterEmployeeWithStructuredAssignment =
  CostCenterEmployeeWithStructuredAssignment & {
    percentage: number
    startDate?: string
    jobCatalogFamilyName?: TextValue
    jobCatalogFunctionName?: TextValue
    jobCatalogRoleName?: TextValue
    isChild?: boolean
  }

const CostCentersPeopleAssignment = ({
  costCenters: _costCenters,
}: {
  costCenters: CostCenterDetails[]
}) => {
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const { company } = useCurrentSession()

  const client = useApolloClient()
  const { dev_test_de_verdad, canManageCostCenters, hasFeatureCostCenterManagement } =
    useOutletContext<{
      dev_test_de_verdad: boolean
      canManageCostCenters: boolean
      hasFeatureCostCenterManagement: boolean
    }>()

  const mapData = useUnsafeCallback((data: CostCentersEmployeesStructuredAssignmentsQuery) => {
    const edges = data.employees.employeesConnection.edges
    return edges.map(({ node }) => ({
      ...node,
      jobCatalogFamilyName: familyLabelForParent(node),
      jobCatalogFunctionName: functionLabelForParent(node),
      jobCatalogRoleName: roleLabelForParent(node),
      startDate: node.structuredAssignments.edges[0]?.node?.startDate,
      percentage: node.structuredAssignments.edges.reduce(
        (acc, value) => acc + Number(value.node.percentage),
        0
      ),
    }))
  }, [])

  const filterByAssignmentStatus = useUnsafeCallback((assignmentStatus?: boolean[]) => {
    if (
      assignmentStatus === undefined ||
      assignmentStatus.length === 0 ||
      (assignmentStatus.includes(true) && assignmentStatus.includes(false))
    ) {
      return {}
    }

    return { withCostCenterAssignments: assignmentStatus.includes(true) }
  }, [])

  const filterByPeopleStatus = useUnsafeCallback((status?: string[]) => {
    if (!status?.length || (status.includes(TERMINATED) && status.includes(ACTIVE))) {
      return {}
    }

    return { accessStatus: status }
  }, [])

  const mapChildren = useUnsafeCallback(
    (item: FormattedCostCenterEmployeeWithStructuredAssignment) => {
      return Promise.resolve({
        records: item.structuredAssignments.edges.map(({ node }) => ({
          ...item,
          jobCatalogFamilyName: familyLabelForChild(node, item),
          jobCatalogFunctionName: functionLabelForChild(node, item),
          jobCatalogRoleName: roleLabelForChild(node, item),
          percentage: Number(node.percentage),
          startDate: '',
          isChild: true,
        })),
        type: 'detailed' as const,
      })
    },
    []
  )

  const source = useGraphqlDataCollectionSource({
    document: CostCentersEmployeesStructuredAssignmentsDocument,
    mapData: (tableData: CostCentersEmployeesStructuredAssignmentsQuery) => mapData(tableData),
    sortings: {
      fullName: {
        label: i18n.t('finance.account_list.headers.name'),
        sortBy: 'fullName',
      },
    },
    defaultSortings: {
      field: 'fullName' as const,
      order: 'asc' as const,
    },
    search: {
      enabled: true,
    },
    filters: {
      legalEntityIds: {
        type: 'in' as const,
        label: i18n.t('finance.cost_centers_list.filters.legal_entity'),
        options: {
          options: company.legalEntities.map(({ id, legalName }) => ({
            value: id,
            label: legalName || '',
          })),
        },
      },
      costCenterIds: {
        type: 'in' as const,
        label: i18n.t('finance.cost_centers_list.filters.cost_center.label'),
        options: {
          options: async () => {
            const { data } = await client.query({
              query: CostCentersListDocument,
              fetchPolicy: 'cache-first',
            })

            return data.costCenters.costCentersConnection.edges.map((costCenter) => ({
              label: costCenter.node.name,
              value: costCenter.node.id,
            }))
          },
        },
      },
      employeeIds: {
        type: 'in' as const,
        label: i18n.t('finance.settings.cost_centers.people'),
        options: {
          options: async () => {
            const { data } = await client.query({
              query: AvatarNameSelectorDocument,
              variables: {
                companyId: company.id,
                includeTeams: false,
                includeWorkplaces: false,
              },
              fetchPolicy: 'cache-first',
            })

            return data.employees.employeeNames.map((employee) => ({
              label: employee.fullName,
              value: employee.id,
            }))
          },
        },
      },
      peopleStatus: {
        type: 'in' as const,
        label: i18n.t('finance.cost_center_people_list.people_status'),
        options: {
          options: [
            { label: i18n.t('employees.employees_list.status.active'), value: ACTIVE },
            { label: i18n.t('employees.employees_list.status.terminated'), value: TERMINATED },
          ],
        },
      },
      teamIds: {
        type: 'in' as const,
        label: i18n.t('filters.filter.team'),
        options: {
          options: async () => {
            const { data } = await client.query({
              query: EmployeesTeamsDocument,
              fetchPolicy: 'cache-first',
            })

            return data.teams.teams.map((team) => ({
              label: team.name,
              value: team.id,
            }))
          },
        },
      },
    },
    presetsLoading: false,
    mapVariables: ({
      search,
      sortings,
      filters,
    }: {
      search?: string
      filters?: Filters
      sortings?: Array<{ field: string; order: string }>
    }) => {
      return {
        search: search,
        sortOrder: sortings?.[0],
        legalEntityIds: filters?.legalEntityIds?.length ? filters?.legalEntityIds : undefined,
        teamIds: filters?.teamIds?.length ? filters?.teamIds : undefined,
        costCenterIds: filters?.costCenterIds?.length ? filters?.costCenterIds : undefined,
        employeeIds: filters?.employeeIds?.length ? filters?.employeeIds : undefined,
        ...filterByAssignmentStatus(filters?.withCostCenterAssignments),
        ...filterByPeopleStatus(filters?.peopleStatus),
      }
    },
    mapPagination: (tableData: CostCentersEmployeesStructuredAssignmentsQuery) => ({
      total: tableData.employees.employeesConnection.totalCount ?? 0,
    }),
    totalItemSummary: (totalCount: number) =>
      i18n.tp('finance.cost_center_people_list.count_employee_assignments', {
        count: totalCount,
      }),
    itemOnClick: (item: FormattedCostCenterEmployeeWithStructuredAssignment) => () => {
      navigate(navigation.settings.finance.costCenters.people.edit.fullPath({ id: item.id }))
    },
    itemsWithChildren: (item: FormattedCostCenterEmployeeWithStructuredAssignment) => {
      return !item.isChild && item.structuredAssignments.edges.length > 0
    },
    fetchChildren: ({ item }: { item: FormattedCostCenterEmployeeWithStructuredAssignment }) => {
      return mapChildren(item)
    },
  })

  // if (costCenters.length === 0) {
  //   return <EmptyCostCenters />
  // }

  return (
    <>
      <OneDataCollection
        source={source}
        onBulkAction={(_, selectedItems) => {
          const checkedItems = selectedItems.itemsStatus.filter((item) => item.checked)
          const employeeIds = checkedItems.map((item) => item.item.id)
          const legalEntityIds = [checkedItems[0]?.item.legalEntity?.id]

          navigate(navigation.settings.finance.costCenters.people.create.fullPath(), {
            state: {
              employeeIds,
              legalEntityIds,
            },
          })
        }}
        visualizations={[
          {
            type: 'table',
            options: {
              frozenColumns: 1,
              columns: [
                {
                  sorting: 'fullName',
                  label: i18n.t('finance.cost_centers_list.headers.name'),
                  render: (item: FormattedCostCenterEmployeeWithStructuredAssignment) => {
                    if (item.isChild) {
                      return {
                        type: 'text',
                        value: {
                          placeholder: i18n.t(
                            'finance.cost_centers_list.headers.job_catalog.not_applicable'
                          ),
                        },
                      }
                    }

                    return {
                      type: 'person',
                      value: {
                        firstName: item.firstName ?? '',
                        lastName: item.lastName ?? '',
                        src: buildAvatarUrl(item.avatar?.url),
                        deactivated: !item.active,
                      },
                    }
                  },
                },
                {
                  label: i18n.t('finance.cost_centers_list.headers.legal_entity'),
                  render: (item: FormattedCostCenterEmployeeWithStructuredAssignment) => {
                    if (item.isChild) {
                      return {
                        type: 'text',
                        value: {
                          placeholder: i18n.t(
                            'finance.cost_centers_list.headers.job_catalog.not_applicable'
                          ),
                        },
                      }
                    }

                    return {
                      type: 'text',
                      value: item.legalEntity?.legalName,
                    }
                  },
                },
                {
                  label: i18n.t('finance.cost_centers_list.headers.job_catalog.family'),
                  render: (item: FormattedCostCenterEmployeeWithStructuredAssignment) => {
                    return {
                      type: 'text',
                      value: item.jobCatalogFamilyName,
                    }
                  },
                },
                {
                  label: i18n.t('finance.cost_centers_list.headers.job_catalog.function'),
                  render: (item: FormattedCostCenterEmployeeWithStructuredAssignment) => {
                    return {
                      type: 'text',
                      value: item.jobCatalogFunctionName,
                    }
                  },
                },
                {
                  label: i18n.t('finance.cost_centers_list.headers.job_catalog.role'),
                  render: (item: FormattedCostCenterEmployeeWithStructuredAssignment) => {
                    return {
                      type: 'text',
                      value: item.jobCatalogRoleName,
                    }
                  },
                },
                {
                  label: i18n.t('finance.cost_centers_list.headers.job_catalog.percentage'),
                  render: (item: FormattedCostCenterEmployeeWithStructuredAssignment) => {
                    return {
                      type: 'text',
                      value: `${(item.percentage * 100).toFixed(2)}%`,
                    }
                  },
                },
                {
                  label: i18n.t('finance.cost_centers_list.headers.job_catalog.start_date'),
                  render: (item: FormattedCostCenterEmployeeWithStructuredAssignment) => {
                    return {
                      type: 'text',
                      value: item.startDate ? moment(item.startDate).format('L') : '',
                    }
                  },
                },
              ],
            },
          },
        ]}
      />
      <Outlet
        context={{
          dev_test_de_verdad,
          canManageCostCenters,
          hasFeatureCostCenterManagement,
        }}
      />
    </>
  )
}

export { CostCentersPeopleAssignment }
